import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useCountryListQuery } from 'api/country';
import { TablePaginationConfig, TableProps } from 'components';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { CountryDataType } from './index';
import { OrderType, PaginationType, SortParamsType } from 'typings';

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};

export const useCountries = () => {
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [sort, setSort] = useState<SortParamsType>(initialSort);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  const { data: { data: { content = [], totalElements = 0 } = {} } = {}, isLoading } = useCountryListQuery({
    size: pagination.size,
    page: pagination.page,
    sortField: sort.field,
    sortOrder: sort.order,
    search,
    filters,
  });
  const onOpenFilters = useCallback(() => setOpenFilters(true), []);
  const onCloseFilters = useCallback(() => setOpenFilters(false), []);

  const routeChange = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.country.view}/${value}/${ADMIN_ROUTES.country.management}`);
    },
    [navigate]
  );

  const handleTableChange: TableProps<CountryDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<CountryDataType> | SorterResult<CountryDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  const onSearch = useCallback((value: string) => {
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setSearch(value);
  }, []);

  const applyFilters = useCallback(
    (values: Record<string, any>) => {
      const _filters: any = {
        ...(values.countryIds && { countryId: values.countryIds }),
        ...(values.tags?.length && { tags: values.tags }),
        ...(values.regionIds && { regionId: values.regionIds }),
        ...(values.subRegions && { subRegion: values.subRegions }),
        ...(values.languageIds && { languageId: values.languageIds }),
        ...(values.lastUpdatedDate?.[0] && {
          lastUpdatedDateFrom: dateFormatter(values.lastUpdatedDate[0], 'start'),
        }),
        ...(values.lastUpdatedDate?.[1] && {
          lastUpdatedDateTo: dateFormatter(values.lastUpdatedDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );
  const resetFilters = useCallback(() => setFilters({}), []);

  return {
    onOpenFilters,
    onCloseFilters,
    routeChange,
    data: content?.length ? content : [],
    total: totalElements ?? 0,
    handleTableChange,
    openFilters,
    applyFilters,
    resetFilters,
    pagination,
    isLoading,
    onSearch,
  };
};
